/**
 * Server state (TanStack Query). Query keys are arrays starting with the resource name, so
 * mutations invalidate by prefix (e.g. ['issues'] after any issue change).
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { api, type Enums, type IssueQuery } from './client';

const MIN = 60_000;

export const keys = {
  enums: ['enums'] as const,
  projects: ['projects'] as const,
  project: (id: number) => ['projects', id] as const,
  categories: (id: number) => ['projects', id, 'categories'] as const,
  versions: (id: number) => ['projects', id, 'versions'] as const,
  handlers: (id: number) => ['projects', id, 'handlers'] as const,
  members: (id: number) => ['projects', id, 'users'] as const,
  fields: (id: number) => ['projects', id, 'fields'] as const,
  issues: (q: IssueQuery) => ['issues', 'list', q] as const,
  issue: (id: number) => ['issues', id] as const,
  files: (id: number) => ['issues', id, 'files'] as const,
  history: (id: number) => ['issues', id, 'history'] as const,
  config: (options: readonly string[], projectId?: number) =>
    ['config', projectId ?? 0, ...options] as const,
  preferences: (projectId?: number) => ['preferences', projectId ?? 0] as const,
  profiles: ['profiles'] as const,
  filters: ['filters'] as const,
  tags: (q: object) => ['tags', q] as const,
  users: (q: object) => ['users', q] as const,
  user: (id: number) => ['users', id] as const,
  summary: (projectId: number | undefined, days: number) =>
    ['summary', projectId ?? 0, days] as const,
};

export const useEnums = () =>
  useQuery({ queryKey: keys.enums, queryFn: api.enums, staleTime: 10 * MIN });
export const useProjects = () =>
  useQuery({ queryKey: keys.projects, queryFn: api.projects.list, staleTime: 5 * MIN });
export const useProject = (id: number | undefined) =>
  useQuery({
    queryKey: keys.project(id ?? 0),
    queryFn: () => api.projects.get(id!),
    enabled: !!id,
  });
export const useCategories = (id: number | undefined) =>
  useQuery({
    queryKey: keys.categories(id ?? 0),
    queryFn: () => api.projects.categories(id!),
    enabled: !!id,
    staleTime: 5 * MIN,
  });
export const useVersions = (id: number | undefined) =>
  useQuery({
    queryKey: keys.versions(id ?? 0),
    queryFn: () => api.projects.versions(id!, { inherit: true }),
    enabled: !!id,
    staleTime: 5 * MIN,
  });
export const useHandlers = (id: number | undefined) =>
  useQuery({
    queryKey: keys.handlers(id ?? 0),
    queryFn: () => api.projects.handlers(id!),
    enabled: !!id,
    staleTime: 5 * MIN,
  });
export const useFields = (id: number | undefined) =>
  useQuery({
    queryKey: keys.fields(id ?? 0),
    queryFn: () => api.projects.fields(id!),
    enabled: !!id,
    staleTime: 5 * MIN,
  });

export const useIssues = (
  q: IssueQuery,
  opts: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.issues.list>>>> = {},
) =>
  useQuery({
    queryKey: keys.issues(q),
    queryFn: () => api.issues.list(q),
    placeholderData: (prev) => prev,
    ...opts,
  });
export const useIssue = (id: number) =>
  useQuery({ queryKey: keys.issue(id), queryFn: () => api.issues.get(id), enabled: id > 0 });

/** Effective config values (only non-private options come back). */
export function useConfig<T extends Record<string, unknown>>(
  options: readonly (keyof T & string)[],
  projectId?: number,
) {
  return useQuery({
    queryKey: keys.config(options, projectId),
    queryFn: async () =>
      Object.fromEntries(
        (await api.config.get([...options], projectId)).map((e) => [e.option, e.value]),
      ) as Partial<T>,
    staleTime: 5 * MIN,
  });
}

export const usePreferences = (projectId?: number) =>
  useQuery({
    queryKey: keys.preferences(projectId),
    queryFn: () => api.preferences.get(projectId),
    staleTime: 5 * MIN,
  });

/** enum lookups: label (or name) for a code, colour for statuses */
export function enumLabel(
  enums: Enums | undefined,
  name: string,
  code: number | undefined | null,
): string {
  if (code === undefined || code === null) return '';
  const v = enums?.[name]?.find((e) => e.code === code);
  return v ? (v.label ?? v.name) : `@${code}@`;
}
